import { clientEnv } from './env.js';

export interface SeoProps {
  title: string;
  description?: string;
  /** Path relative to the site root, e.g. "/property/skyline-luxe". */
  path?: string;
  image?: string;
  type?: 'website' | 'article';
  noindex?: boolean;
  /** JSON-LD structured data object(s). */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

/**
 * Page-level SEO. Uses React 19's native metadata hoisting — <title>, <meta>
 * and <link> rendered here are automatically moved into <head>. JSON-LD is
 * rendered inline (crawlers read it anywhere in the DOM).
 */
/**
 * Serialises a JSON-LD block for inlining inside a `<script>` tag.
 *
 * `JSON.stringify` escapes quotes and backslashes but NOT `<`, so a value
 * containing the literal `</script>` closes the tag early and everything after
 * it is parsed as HTML — an XSS hole. The values here are not ours: property
 * titles and descriptions come from CPzone, the same third-party source the
 * app already runs through DOMPurify before rendering as markup. This closes
 * the equivalent hole on the structured-data path.
 *
 * `<` is a valid JSON escape for `<`, so consumers (Google, validators)
 * parse exactly the same object. U+2028/U+2029 are escaped for the same
 * reason — legal inside a JSON string, but line terminators to a JS parser.
 */
export function serializeJsonLd(block: unknown): string {
  return JSON.stringify(block)
    .replace(/</g, '\\u003c')
    .replace(/[\u2028\u2029]/g, (c) => (c === '\u2028' ? '\\u2028' : '\\u2029'));
}

export function Seo({
  title,
  description,
  path = '/',
  image,
  type = 'website',
  noindex = false,
  jsonLd,
}: SeoProps) {
  const fullTitle = title.includes(clientEnv.siteName)
    ? title
    : `${title} — ${clientEnv.siteName}`;
  const canonical = `${clientEnv.siteUrl}${path}`;
  const ogImage = image ?? `${clientEnv.siteUrl}/og-default.jpg`;
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={clientEnv.siteName} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />

      {blocks.map((block, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: serializeJsonLd(block) }}
        />
      ))}
    </>
  );
}
